import { Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Committee, CommitteeMember } from "@/types/governance";

interface CommitteeTreeProps {
  committees: Committee[];
  members: CommitteeMember[];
  selectedCommitteeId: string | null;
  onSelect: (committeeId: string) => void;
  onAddSubCommittee: (committee: Committee) => void;
  onEditCommittee: (committee: Committee) => void;
}

interface CommitteeTreeNodeProps {
  committee: Committee;
  childrenByParent: Map<string, Committee[]>;
  membersById: Map<string, CommitteeMember>;
  selectedCommitteeId: string | null;
  onSelect: (committeeId: string) => void;
  onAddSubCommittee: (committee: Committee) => void;
  onEditCommittee: (committee: Committee) => void;
}

function CommitteeTreeNode({
  committee,
  childrenByParent,
  membersById,
  selectedCommitteeId,
  onSelect,
  onAddSubCommittee,
  onEditCommittee,
}: CommitteeTreeNodeProps) {
  const levelBadgeClassMap: Record<number, string> = {
    1: "border-violet-300 bg-violet-100 text-violet-800",
    2: "border-blue-300 bg-blue-100 text-blue-800",
    3: "border-emerald-300 bg-emerald-100 text-emerald-800",
    4: "border-amber-300 bg-amber-100 text-amber-800",
    5: "border-rose-300 bg-rose-100 text-rose-800",
  };
  const levelBorderClassMap: Record<number, string> = {
    1: "border-l-violet-500",
    2: "border-l-blue-500",
    3: "border-l-emerald-500",
    4: "border-l-amber-500",
    5: "border-l-rose-500",
  };
  const owner = committee.ownerId ? membersById.get(committee.ownerId) : undefined;
  const children = childrenByParent.get(committee.id) ?? [];

  return (
    <div>
      <button
        type="button"
        className={`w-full rounded-md border border-l-4 px-3 py-2 text-left transition ${
          selectedCommitteeId === committee.id
            ? "border-primary bg-primary/5"
            : "border-transparent hover:border-border hover:bg-accent/60"
        } ${levelBorderClassMap[committee.level] ?? "border-l-slate-500"}`}
        onClick={() => onSelect(committee.id)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{committee.name}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge
                variant="outline"
                className={levelBadgeClassMap[committee.level] ?? "border-slate-300 bg-slate-100 text-slate-800"}
              >
                L{committee.level}
              </Badge>
              {owner ? (
                <Badge variant="secondary" className="max-w-[200px] truncate">
                  Owner: {owner.name}
                </Badge>
              ) : (
                <Badge variant="outline" className="border-amber-300 text-amber-700">
                  No owner
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={(event) => {
                event.stopPropagation();
                onEditCommittee(committee);
              }}
            >
              Edit
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={(event) => {
                event.stopPropagation();
                onAddSubCommittee(committee);
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Sub
            </Button>
          </div>
        </div>
      </button>

      {children.length > 0 ? (
        <div className="mt-1 space-y-1">
          {children.map((child, index) => {
            const isLast = index === children.length - 1;
            return (
              <div key={child.id} className="relative pl-5">
                <span
                  className={`pointer-events-none absolute left-0 border-border/70 ${
                    isLast ? "top-0 h-5 border-l" : "inset-y-0 border-l"
                  }`}
                />
                <span className="pointer-events-none absolute left-0 top-5 w-4 border-t border-border/70" />
                <CommitteeTreeNode
                  committee={child}
                  childrenByParent={childrenByParent}
                  membersById={membersById}
                  selectedCommitteeId={selectedCommitteeId}
                  onSelect={onSelect}
                  onAddSubCommittee={onAddSubCommittee}
                  onEditCommittee={onEditCommittee}
                />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function CommitteeTree({
  committees,
  members,
  selectedCommitteeId,
  onSelect,
  onAddSubCommittee,
  onEditCommittee,
}: CommitteeTreeProps) {
  const membersById = new Map(members.map((member) => [member.id, member]));
  const childrenByParent = committees.reduce<Map<string, Committee[]>>((map, committee) => {
    if (!committee.parentId) return map;
    const children = map.get(committee.parentId) ?? [];
    children.push(committee);
    map.set(committee.parentId, children);
    return map;
  }, new Map());

  const sortedChildrenByParent = new Map<string, Committee[]>();
  childrenByParent.forEach((items, parentId) => {
    sortedChildrenByParent.set(
      parentId,
      [...items].sort((a, b) => a.name.localeCompare(b.name)),
    );
  });

  const rootCommittees = committees
    .filter((committee) => !committee.parentId)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-1">
      {rootCommittees.map((committee) => (
        <CommitteeTreeNode
          key={committee.id}
          committee={committee}
          childrenByParent={sortedChildrenByParent}
          membersById={membersById}
          selectedCommitteeId={selectedCommitteeId}
          onSelect={onSelect}
          onAddSubCommittee={onAddSubCommittee}
          onEditCommittee={onEditCommittee}
        />
      ))}
    </div>
  );
}
